import type { ComponentPropsWithoutRef, Ref } from 'react';
import { cx } from '~/styled-system/css';
import { css } from '@understory-io/leaf/css';

type CheckoutCardProps = ComponentPropsWithoutRef<'div'> & {
  /** Collapsed step wrapper: no top padding and no horizontal padding (inner
   *  sections supply their own). */
  collapse?: boolean;
  /** Flat on tablet+: no raised surface, radius or shadow. */
  flat?: boolean;
  ref?: Ref<HTMLDivElement>;
};

export type { CheckoutCardProps };

/**
 * The card wrapper for a checkout step. Presentational only — designed to be
 * used as the `as` target of a Leaf `Stack` so the caller controls the layout:
 *
 * ```tsx
 * <Stack gap="large" as={CheckoutCard} collapse flat>…</Stack>
 * ```
 */
export function CheckoutCard({
  collapse,
  flat,
  className,
  ref,
  ...props
}: CheckoutCardProps) {
  return (
    <div
      ref={ref}
      className={cx(checkoutCard({ collapse, flat }), className)}
      {...props}
    />
  );
}

function checkoutCard(options?: { collapse?: boolean; flat?: boolean }) {
  return cx(
    css({
      background: 'surface.base',
      borderRadius: 'none',
      tablet: {
        // Collapsed step wrappers take their top spacing (the gap after the
        // Header) from CheckoutMain so every checkout step matches the rest of
        // the storefront (US-9869); inner cards keep symmetric padding.
        paddingTop: options?.collapse ? 'none' : 'large',
        paddingBottom: 'large',
        borderRadius: options?.flat ? 'none' : 'xlarge',
        background: options?.flat ? 'surface.base' : 'surface.raised',
        shadow: options?.flat ? 'none' : 'large',
      },
    }),
    options?.collapse ? collapsedPadding() : cardInlinePadding()
  );
}

export function checkoutCardPadding() {
  return css({
    px: { base: 'medium', tablet: 'none' },
  });
}

// A non-collapsed card (e.g. the info-step price summary) keeps its own
// horizontal padding so its content isn't flush to the edge — unlike the
// full-width step elements that use checkoutCardPadding (US-9870).
function cardInlinePadding() {
  return css({
    px: { base: 'medium', tablet: 'large' },
  });
}

function collapsedPadding() {
  // No horizontal padding: on mobile the inner sections supply their own
  // (checkoutCardPadding), and on tablet+ the step content runs to the full
  // CHECKOUT_MAX_WIDTH (US-9851).
  return css({ px: 'none' });
}
